import React, { useState, useMemo, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { InputDatePicker } from '@/components/ui/InputDatePicker'
import { TimePicker } from '@/components/ui/timePicker'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Check, ChevronsUpDown, PlusIcon } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { BuyerMemberFragment } from '@gql'
import { useBuyerMemberSearch } from '@/user/hooks/useBuyerMemberSearch'

type EstateVisitCreateFormValues = {
  visitDate: Date
  startTime: string
  buyerMemberId: string
}

type EstateVisitCreateButtonProps = {
  enabled: boolean
  visitSlotDuration: number // in minutes
  defaultDate?: string // format: yyyy-MM-dd
  onSubmit: (buyerMemberId: string, startTime: Date, endTime: Date) => void
}

export const EstateVisitCreateButton = ({
  enabled,
  visitSlotDuration,
  defaultDate,
  onSubmit,
}: EstateVisitCreateButtonProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [buyerComboboxOpen, setBuyerComboboxOpen] = useState(false)
  const [selectedBuyerData, setSelectedBuyerData] =
    useState<BuyerMemberFragment | null>(null)

  const {
    searchQuery,
    setSearchQuery,
    buyerMembers,
    loading: searchLoading,
  } = useBuyerMemberSearch()

  const schema = useMemo(
    () =>
      yup.object().shape({
        visitDate: yup.date().required(t('Forms.errors.required')),
        startTime: yup.string().required(t('Forms.errors.required')),
        buyerMemberId: yup.string().required(t('Forms.errors.required')),
      }),
    [t]
  )

  const defaultValues = useMemo(() => {
    let visitDate: Date

    if (defaultDate) {
      // Use the provided default date (format: yyyy-MM-dd)
      visitDate = new Date(defaultDate)
    } else {
      // Fallback to tomorrow
      const tomorrow = new Date()
      tomorrow.setDate(tomorrow.getDate() + 1)
      visitDate = tomorrow
    }

    return {
      visitDate,
      startTime: '09:00',
      buyerMemberId: '',
    }
  }, [defaultDate])

  const form = useForm<EstateVisitCreateFormValues>({
    resolver: yupResolver(schema),
    defaultValues,
  })

  // Reset form when defaultValues change (e.g., when defaultDate changes)
  useEffect(() => {
    form.reset(defaultValues)
  }, [defaultValues, form])

  const selectedBuyerId = form.watch('buyerMemberId')
  const selectedBuyer = selectedBuyerData

  const handleSubmit = async (values: EstateVisitCreateFormValues) => {
    const { visitDate, startTime, buyerMemberId } = values

    // Create new start date with the selected date and time
    const newStartDate = new Date(visitDate)
    const [startHour, startMinute] = startTime.split(':').map(Number)
    newStartDate.setHours(startHour, startMinute, 0, 0)

    // Calculate end date by adding visitSlotDuration minutes
    const newEndDate = new Date(newStartDate)
    newEndDate.setMinutes(newEndDate.getMinutes() + visitSlotDuration)

    onSubmit(buyerMemberId, newStartDate, newEndDate)
    setOpen(false)
    form.reset(defaultValues)
    setSearchQuery('')
    setSelectedBuyerData(null)
  }

  const handleCancel = () => {
    setOpen(false)
    form.reset(defaultValues)
    setSearchQuery('')
    setSelectedBuyerData(null)
  }

  const handleBuyerSelect = (buyer: BuyerMemberFragment) => {
    form.setValue('buyerMemberId', buyer.id)
    setSelectedBuyerData(buyer)
    setBuyerComboboxOpen(false)
    setSearchQuery('')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(newOpen) => {
        // Prevent the dialog from closing when the buyer combobox is open
        if (!newOpen && buyerComboboxOpen) {
          return
        }
        setOpen(newOpen)
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="default"
          size="sm"
          disabled={!enabled}
          onClick={() => setOpen(true)}
        >
          <PlusIcon className="tw-h-4 tw-w-4 tw-mr-2" />
          {t('EstateVisitCreateButton.title')}
        </Button>
      </DialogTrigger>
      <DialogContent className="tw-min-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('EstateVisitCreateButton.title')}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            className="tw-space-y-6"
          >
            <FormField
              control={form.control}
              name="visitDate"
              render={({ field }) => (
                <FormItem className="tw-flex tw-flex-col">
                  <FormLabel>
                    {t('EstateVisitCreateButton.visitDate')}
                  </FormLabel>
                  <InputDatePicker
                    {...field}
                    fromYear={new Date().getFullYear()}
                    toYear={new Date().getFullYear() + 2}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="startTime"
              render={({ field }) => (
                <FormItem className="tw-flex tw-flex-col tw-w-full">
                  <FormLabel>
                    {t('EstateVisitCreateButton.startTime')}
                  </FormLabel>
                  <TimePicker
                    {...field}
                    startTime="00:00"
                    endTime="24:00"
                    step={visitSlotDuration}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="buyerMemberId"
              render={({ field }) => (
                <FormItem className="tw-flex tw-flex-col">
                  <FormLabel>
                    {t('EstateVisitCreateButton.buyerMember')}
                  </FormLabel>
                  <Popover
                    modal={true}
                    open={buyerComboboxOpen}
                    onOpenChange={setBuyerComboboxOpen}
                  >
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        role="combobox"
                        aria-expanded={buyerComboboxOpen}
                        className="tw-justify-between"
                      >
                        {selectedBuyer
                          ? `${selectedBuyer.user?.displayName} (${selectedBuyer.user?.email})`
                          : t('EstateVisitCreateButton.selectBuyer')}
                        <ChevronsUpDown className="tw-ml-2 tw-h-4 tw-w-4 tw-shrink-0 tw-opacity-50" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="tw-w-[--radix-popover-trigger-width] tw-p-0">
                      <Command shouldFilter={false}>
                        <CommandInput
                          placeholder={t('EstateVisitCreateButton.searchBuyer')}
                          value={searchQuery}
                          onValueChange={setSearchQuery}
                        />
                        <CommandList>
                          <CommandEmpty>
                            {searchLoading
                              ? t('EstateVisitCreateButton.searching')
                              : t('EstateVisitCreateButton.noBuyerFound')}
                          </CommandEmpty>
                          <CommandGroup>
                            {buyerMembers.map((buyer) => (
                              <CommandItem
                                key={buyer.id}
                                value={`${buyer.user?.displayName} ${buyer.user?.email}`}
                                onSelect={() => {
                                  console.log(
                                    'CommandItem onSelect called for:',
                                    buyer
                                  )
                                  handleBuyerSelect(buyer)
                                }}
                                className="tw-cursor-pointer"
                              >
                                <Check
                                  className={cn(
                                    'tw-mr-2 tw-h-4 tw-w-4',
                                    selectedBuyerId === buyer.id
                                      ? 'tw-opacity-100'
                                      : 'tw-opacity-0'
                                  )}
                                />
                                <span className="tw-font-medium">
                                  {buyer.user?.displayName}
                                </span>
                                <span className="tw-text-sm tw-text-muted-foreground">
                                  {buyer.user?.email}
                                </span>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="tw-flex tw-justify-end tw-space-x-2">
              <Button type="button" variant="outline" onClick={handleCancel}>
                {t('common.cancel')}
              </Button>
              <Button type="submit">{t('common.create')}</Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
